import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Grid,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrg } from '../context/OrgContext';
import { useAuth } from '../context/AuthContext';
import { chalanAPI, vehicleTypeAPI } from '../services/chalanService';
import { BACKEND_BASE_URL } from '../services/apiClient';
import PermissionTooltip from '../components/PermissionTooltip';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DoneAll as PaidIcon,
  CheckCircle as ApproveIcon,
  HighlightOff as RejectIcon,
  Description as DocIcon,
  CloudUpload as CloudUploadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';

function ChalanDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const orgContext = useOrg();
  const { user } = useAuth();
  const orgPrefix = `/${(orgContext?.orgCode || localStorage.getItem('orgCode') || 'rta').toLowerCase()}`;
  const backPath = location.state?.returnTo || `${orgPrefix}/chalans`;

  const canManageChalan = user?.is_staff || user?.features?.some(f => f.name === 'chalan_update' || f === 'chalan_update') || user?.role?.features?.some(f => f.name === 'chalan_update' || f === 'chalan_update');
  const [chalan, setChalan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});

  // Document upload state
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Dialog states
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [feeDialog, setFeeDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_amount: '',
    payment_reference: '',
  });
  const [feeData, setFeeData] = useState({ fees_amount: '' });

  const statusColors = {
    pending: 'warning',
    issued: 'info',
    paid: 'success',
    cancelled: 'error',
    disputed: 'error',
    resolved: 'success',
  };

  // Fetch chalan details
  useEffect(() => {
    const fetchChalan = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await chalanAPI.getChalan(id);
        setChalan(response.data);
        setEditData(response.data);
        setPaymentData({
          payment_amount: response.data.remaining_amount || response.data.fees_amount,
          payment_reference: '',
        });
        setFeeData({ fees_amount: response.data.fees_amount });
      } catch (err) {
        console.error('Error fetching chalan:', err);
        setError(t('chalanDetail.errorLoadFailed'));
      } finally {
        setLoading(false);
      }
    };
    fetchChalan();
  }, [id, t]);

  // Fetch vehicle types
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        const response = await vehicleTypeAPI.getVehicleTypes();
        setVehicleTypes(response.data.results || response.data || []);
      } catch (err) {
        console.error('Error fetching vehicle types:', err);
      }
    };
    fetchVehicleTypes();
  }, []);

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApprove = async () => {
    try {
      setLoading(true);
      await chalanAPI.approveChalan(id);
      const approvedRes = await chalanAPI.getChalan(id);
      setChalan(approvedRes.data);
      setSuccess(t('chalanDetail.successApproved'));
    } catch (err) {
      console.error('Error approving chalan:', err);
      setError(t('chalanDetail.errorApproveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    try {
      setLoading(true);
      await chalanAPI.rejectChalan(id);
      const rejectedRes = await chalanAPI.getChalan(id);
      setChalan(rejectedRes.data);
      setSuccess(t('chalanDetail.successRejected'));
    } catch (err) {
      console.error('Error rejecting chalan:', err);
      setError(t('chalanDetail.errorRejectFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChalan = async () => {
    try {
      setDeleting(true);
      await chalanAPI.deleteChalan(id);
      navigate(backPath, { state: { deletedChalan: chalan.chalan_number } });
    } catch (err) {
      console.error('Error deleting chalan:', err);
      setError(err.response?.data?.error || t('chalanDetail.errorDeleteFailed'));
      setDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      const updateData = {
        owner_name: editData.owner_name,
        owner_phone: editData.owner_phone,
        violation_description: editData.violation_description,
        issue_location: editData.issue_location,
        remarks: editData.remarks,
      };

      await chalanAPI.updateChalan(id, updateData);
      // Refetch full chalan data to ensure all fields are populated
      const updatedRes = await chalanAPI.getChalan(id);
      setChalan(updatedRes.data);
      setIsEditing(false);
      setSuccess(t('chalanDetail.successUpdated'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('chalanDetail.errorUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async () => {
    try {
      setLoading(true);
      await chalanAPI.markAsPaid(id, paymentData);
      setSuccess(t('chalanDetail.successMarkedPaid'));
      setPaymentDialog(false);
      // Refresh chalan
      const response = await chalanAPI.getChalan(id);
      setChalan(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.response?.data?.message || t('chalanDetail.errorMarkPaidFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFees = async () => {
    try {
      setLoading(true);
      await chalanAPI.updateFees(id, feeData);
      setSuccess(t('chalanDetail.successFeesUpdated'));
      setFeeDialog(false);
      // Refresh chalan
      const response = await chalanAPI.getChalan(id);
      setChalan(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('chalanDetail.errorFeesUpdateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getVehicleTypeName = (vehicleTypeId) => {
    const vType = vehicleTypes.find((vt) => vt.id === vehicleTypeId);
    return vType ? vType.name : t('permitSearch.na');
  };

  const handleDocumentUpload = async () => {
    if (!docFile) return;
    try {
      setUploadingDoc(true);
      await chalanAPI.uploadDocument(id, docFile);
      const response = await chalanAPI.getChalan(id);
      setChalan(response.data);
      setDocFile(null);
      setSuccess(t('chalanDetail.successDocUploaded'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || t('chalanDetail.errorDocUploadFailed'));
    } finally {
      setUploadingDoc(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !chalan) {
    return (
      <Box sx={{ padding: '20px' }}>
        <Alert severity="error">{error}</Alert>
        <Button
          variant="outlined"
          onClick={() => navigate(backPath)}
          sx={{ marginTop: '20px' }}
        >
          {t('chalanDetail.backToChalans')}
        </Button>
      </Box>
    );
  }

  if (!chalan) {
    return (
      <Box sx={{ padding: '20px' }}>
        <Alert severity="info">{t('chalanDetail.notFound')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: '1000px', margin: '0 auto', minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mb: 2.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', overflowWrap: 'anywhere' }}>
          {t('chalanDetail.chalanNumberTitle', { number: chalan.chalan_number })}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {chalan.status === 'pending' && !isEditing && (
            <>
              <PermissionTooltip title={!canManageChalan ? t('chalanDetail.noPermApprove') : ''}>
                <span>
                  <Button
                    variant="contained"
                    color="info"
                    startIcon={<ApproveIcon />}
                    onClick={handleApprove}
                    disabled={loading || !canManageChalan}
                    sx={{ marginRight: '10px' }}
                  >
                    {t('chalanDetail.approve')}
                  </Button>
                </span>
              </PermissionTooltip>
              <PermissionTooltip title={!canManageChalan ? t('chalanDetail.noPermReject') : ''}>
                <span>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={handleReject}
                    disabled={loading || !canManageChalan}
                    sx={{ marginRight: '10px' }}
                  >
                    {t('chalanDetail.reject')}
                  </Button>
                </span>
              </PermissionTooltip>
            </>
          )}
          <Button
            variant="outlined"
            onClick={() => navigate(backPath)}
            sx={{ marginRight: '10px' }}
          >
            {t('common.back')}
          </Button>
          {!isEditing && (
            <PermissionTooltip title={!user?.is_staff ? t('chalanDetail.noPermDelete') : ''}>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDeleteDialog(true)}
                  disabled={!user?.is_staff}
                  sx={{ marginRight: '10px' }}
                >
                  {t('common.delete')}
                </Button>
              </span>
            </PermissionTooltip>
          )}
          {!isEditing && (
            <PermissionTooltip title={!canManageChalan ? t('chalanDetail.noPermEdit') : ''}>
              <span>
                <Button
                  variant={canManageChalan ? 'contained' : 'outlined'}
                  startIcon={<EditIcon />}
                  onClick={() => setIsEditing(true)}
                  disabled={!canManageChalan}
                  sx={canManageChalan ? {} : { bgcolor: '#fff', color: '#aaa', borderColor: '#ddd' }}
                >
                  {t('common.edit')}
                </Button>
              </span>
            </PermissionTooltip>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ marginBottom: '20px' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ marginBottom: '20px' }}>{success}</Alert>}

      {/* Status Card */}
      <Card sx={{ marginBottom: '20px', backgroundColor: '#f5f5f5' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography color="textSecondary" gutterBottom>
                {t('permitList.status')}
              </Typography>
              <Chip
                label={t(`common.${chalan.status}`, { defaultValue: chalan.status }).toUpperCase()}
                color={statusColors[chalan.status] || 'default'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography color="textSecondary" gutterBottom>
                {t('chalanDetail.createdDate')}
              </Typography>
              <Typography>
                {new Date(chalan.issued_date).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography color="textSecondary" gutterBottom>
                {t('chalanDetail.totalFee')}
              </Typography>
              <Typography sx={{ fontWeight: 'bold', color: '#f44336' }}>
                Rs. {parseFloat(chalan.fees_amount || 0).toFixed(2)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography color="textSecondary" gutterBottom>
                {t('chalanDetail.paidAmount')}
              </Typography>
              <Typography sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                Rs. {parseFloat(chalan.paid_amount || 0).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Owner Information */}
      <Paper sx={{ padding: '20px', marginBottom: '20px' }}>
        <Typography variant="h6" sx={{ marginBottom: '15px', fontWeight: 'bold' }}>
          {t('permitSearch.ownerInfo')}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanDetail.name')}
            </Typography>
            {isEditing ? (
              <TextField
                name="owner_name"
                value={editData.owner_name}
                onChange={handleEditChange}
                fullWidth
                size="small"
              />
            ) : (
              <Typography>{chalan.owner_name}</Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('permitSearch.cnicLabel')}
            </Typography>
            <Typography>{chalan.owner_cnic}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanDetail.phone')}
            </Typography>
            {isEditing ? (
              <TextField
                name="owner_phone"
                value={editData.owner_phone || ''}
                onChange={handleEditChange}
                fullWidth
                size="small"
              />
            ) : (
              <Typography>{chalan.owner_phone || t('permitSearch.na')}</Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanDetail.carNumber')}
            </Typography>
            <Typography>{chalan.car_number}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Violation Information */}
      <Paper sx={{ padding: '20px', marginBottom: '20px' }}>
        <Typography variant="h6" sx={{ marginBottom: '15px', fontWeight: 'bold' }}>
          {t('chalanDetail.violationInfo')}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanList.vehicleType')}
            </Typography>
            <Typography>{getVehicleTypeName(chalan.vehicle_type)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('createChalan.issueLocationLabel')}
            </Typography>
            {isEditing ? (
              <TextField
                name="issue_location"
                value={editData.issue_location || ''}
                onChange={handleEditChange}
                fullWidth
                size="small"
              />
            ) : (
              <Typography>{chalan.issue_location || t('permitSearch.na')}</Typography>
            )}
          </Grid>
          <Grid size={12}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanDetail.violationDescription')}
            </Typography>
            {isEditing ? (
              <TextField
                name="violation_description"
                value={editData.violation_description}
                onChange={handleEditChange}
                fullWidth
                multiline
                rows={3}
                size="small"
              />
            ) : (
              <Typography>{chalan.violation_description}</Typography>
            )}
          </Grid>
          <Grid size={12}>
            <Typography color="textSecondary" gutterBottom>
              {t('createChalan.remarksLabel')}
            </Typography>
            {isEditing ? (
              <TextField
                name="remarks"
                value={editData.remarks || ''}
                onChange={handleEditChange}
                fullWidth
                multiline
                rows={2}
                size="small"
              />
            ) : (
              <Typography>{chalan.remarks || t('permitSearch.na')}</Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Payment Information */}
      <Paper sx={{ padding: '20px', marginBottom: '20px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', overflowWrap: 'anywhere' }}>
            {t('chalanDetail.paymentInfo')}
          </Typography>
          <Box>
            {chalan.status !== 'paid' && chalan.status !== 'cancelled' && (
              <>
                <PermissionTooltip title={!canManageChalan ? t('chalanDetail.noPermMarkPaid') : ''}>
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<PaidIcon />}
                      onClick={() => setPaymentDialog(true)}
                      disabled={!canManageChalan}
                      sx={{ marginRight: '10px' }}
                    >
                      {t('chalanDetail.markAsPaid')}
                    </Button>
                  </span>
                </PermissionTooltip>
                <PermissionTooltip title={!canManageChalan ? t('chalanDetail.noPermUpdateFees') : ''}>
                  <span>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => setFeeDialog(true)}
                      disabled={!canManageChalan}
                    >
                      {t('chalanDetail.updateFees')}
                    </Button>
                  </span>
                </PermissionTooltip>
              </>
            )}
          </Box>
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanDetail.totalFee')}
            </Typography>
            <Typography sx={{ fontWeight: 'bold', overflowWrap: 'anywhere' }}>
              Rs. {parseFloat(chalan.fees_amount || 0).toFixed(2)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanDetail.paidAmount')}
            </Typography>
            <Typography sx={{ fontWeight: 'bold', overflowWrap: 'anywhere' }}>
              Rs. {parseFloat(chalan.paid_amount || 0).toFixed(2)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography color="textSecondary" gutterBottom>
              {t('chalanDetail.remaining')}
            </Typography>
            <Typography sx={{ fontWeight: 'bold', color: '#f44336' }}>
              Rs. {parseFloat(chalan.remaining_amount || 0).toFixed(2)}
            </Typography>
          </Grid>
          {chalan.payment_date && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography color="textSecondary" gutterBottom>
                {t('chalanDetail.paymentDate')}
              </Typography>
              <Typography>
                {new Date(chalan.payment_date).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Document Section */}
      <Paper sx={{ padding: '20px', marginBottom: '20px' }}>
        <Typography variant="h6" sx={{ marginBottom: '15px', fontWeight: 'bold' }}>
          {t('chalanDetail.document')}
        </Typography>
        {chalan.document ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <DocIcon color="primary" />
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon />}
              href={chalan.document.startsWith('http') ? chalan.document : `${BACKEND_BASE_URL}${chalan.document}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('chalanDetail.viewDocument')}
            </Button>
          </Box>
        ) : (
          <Typography color="textSecondary" sx={{ mb: 2 }}>{t('chalanDetail.noDocument')}</Typography>
        )}
        {canManageChalan && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              size="small"
            >
              {docFile ? docFile.name : t('chalanDetail.chooseFile')}
              <input
                type="file"
                hidden
                accept="image/*,application/pdf"
                onChange={(e) => setDocFile(e.target.files[0] || null)}
              />
            </Button>
            {docFile && (
              <Button
                variant="contained"
                size="small"
                onClick={handleDocumentUpload}
                disabled={uploadingDoc}
              >
                {uploadingDoc ? <CircularProgress size={18} /> : t('chalanDetail.upload')}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* Edit Actions */}
      {isEditing && canManageChalan && (
        <Box sx={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveEdit}
            color="success"
            disabled={loading}
          >
            {t('chalanDetail.saveChanges')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => setIsEditing(false)}
          >
            {t('common.cancel')}
          </Button>
        </Box>
      )}

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('chalanDetail.markPaidTitle')}</DialogTitle>
        <DialogContent sx={{ paddingTop: '20px' }}>
          <TextField
            fullWidth
            label={t('chalanDetail.paymentAmount')}
            type="number"
            inputProps={{ step: '0.01' }}
            value={paymentData.payment_amount}
            onChange={(e) => setPaymentData({ ...paymentData, payment_amount: e.target.value })}
            sx={{ marginBottom: '15px' }}
          />
          <TextField
            fullWidth
            label={t('chalanDetail.paymentReference')}
            value={paymentData.payment_reference}
            onChange={(e) => setPaymentData({ ...paymentData, payment_reference: e.target.value })}
            placeholder={t('chalanDetail.paymentRefPlaceholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleMarkAsPaid}
            variant="contained"
            color="success"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : t('chalanDetail.markAsPaid')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fee Dialog */}
      <Dialog open={feeDialog} onClose={() => setFeeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('chalanDetail.updateFeesTitle')}</DialogTitle>
        <DialogContent sx={{ paddingTop: '20px' }}>
          <TextField
            fullWidth
            label={t('chalanDetail.newFeeAmount')}
            type="number"
            inputProps={{ step: '0.01' }}
            value={feeData.fees_amount}
            onChange={(e) => setFeeData({ fees_amount: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeeDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleUpdateFees}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : t('chalanDetail.updateFees')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('chalanDetail.deleteChalanTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('chalanDetail.deleteChalanConfirm', { number: chalan.chalan_number })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>{t('common.cancel')}</Button>
          <Button
            onClick={handleDeleteChalan}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChalanDetail;
